/**
 * ApproveTransferModal Component
 * Confirmation dialog before approving and executing a transfer.
 * Shows a summary of the transfer amounts and collects bank reference before the user confirms.
 */

import { useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import type { TransferPending } from '@/types/collection.types';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(value);

const formatDate = (dateStr: string) =>
  new Date(dateStr + 'T12:00:00').toLocaleDateString('es-MX', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

interface ApproveTransferModalProps {
  isOpen: boolean;
  onConfirm: (bankReference: string) => void;
  onCancel: () => void;
  isApproving: boolean;
  transfer: TransferPending | null;
}

export function ApproveTransferModal({
  isOpen,
  onConfirm,
  onCancel,
  isApproving,
  transfer,
}: ApproveTransferModalProps) {
  const [bankReference, setBankReference] = useState('');

  if (!transfer) return null;

  const summaryItems = [
    { label: 'Periodo', value: `${formatDate(transfer.period_start)} - ${formatDate(transfer.period_end)}` },
    { label: 'Principal', value: formatCurrency(transfer.total_principal_mxn) },
    { label: 'Interes UNE-Credito', value: formatCurrency(transfer.total_partner_interest_mxn) },
    { label: 'Interes Fiado', value: formatCurrency(transfer.total_fiado_interest_mxn) },
    { label: 'Registros', value: String(transfer.records_count) },
  ];

  function handleConfirm() {
    onConfirm(bankReference);
    setBankReference('');
  }

  function handleCancel() {
    setBankReference('');
    onCancel();
  }

  return (
    <Modal isOpen={isOpen} onClose={handleCancel} title="Confirmar Transferencia" size="sm">
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Estas a punto de aprobar la transferencia a UNE-Credito.
          Verifica los montos e ingresa la referencia bancaria antes de confirmar.
        </p>

        {/* Summary */}
        <div className="space-y-2 rounded-lg border border-border bg-muted/30 p-4">
          {(summaryItems ?? []).map((item) => (
            <div key={item.label} className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{item.label}</span>
              <span className="font-medium text-foreground">{item.value}</span>
            </div>
          ))}

          {/* Total highlight */}
          <div className="mt-2 border-t border-border pt-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-foreground">Total a Transferir</span>
              <span className="text-lg font-bold text-foreground">
                {formatCurrency(transfer.total_amount_mxn)}
              </span>
            </div>
          </div>
        </div>

        {/* Bank Reference Input */}
        <div>
          <label htmlFor="bankReference" className="mb-1 block text-sm font-medium text-foreground">
            Referencia Bancaria
          </label>
          <input
            id="bankReference"
            type="text"
            value={bankReference}
            onChange={(e) => setBankReference(e.target.value)}
            placeholder="Ingresa la referencia bancaria"
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            disabled={isApproving}
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={handleCancel} disabled={isApproving}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleConfirm}
            isLoading={isApproving}
            disabled={!bankReference.trim()}
          >
            Confirmar Transferencia
          </Button>
        </div>
      </div>
    </Modal>
  );
}
